import { gql } from 'graphql-request';

const createShopifyCart = () => {
    return gql`
        mutation cartCreate($input: CartInput) {
            cartCreate(input: $input) {
                cart {
                    id
                    createdAt
                    updatedAt
                    checkoutUrl
                    totalQuantity
                    lines(first: 10) {
                        edges {
                            node {
                                id
                                merchandise {
                                    ... on ProductVariant {
                                        id
                                    }
                                }
                            }
                        }
                    }
                    buyerIdentity {
                        deliveryAddressPreferences {
                            __typename
                        }
                    }
                    attributes {
                        key
                        value
                    }
                    # The estimated total cost of all merchandise that the customer will pay at checkout.
                    cost {
                        totalAmount {
                            amount
                            currencyCode
                        }
                        # The estimated amount, before taxes and discounts, for the customer to pay at checkout.
                        subtotalAmount {
                            amount
                            currencyCode
                        }
                        # The estimated tax amount for the customer to pay at checkout.
                        totalTaxAmount {
                            amount
                            currencyCode
                        }
                        # The estimated duty amount for the customer to pay at checkout.
                        totalDutyAmount {
                            amount
                            currencyCode
                        }
                    }
                }
                userErrors {
                    field
                    message
                }
            }
        }
    `;
};

const retrieveShopifyCart = () => {
    return gql`
        query cart($id: ID!) {
            cart(id: $id) {
                id
                createdAt
                updatedAt
                checkoutUrl
                totalQuantity
                lines(first: 200) {
                    edges {
                        node {
                            id
                            quantity
                            cost {
                                amountPerQuantity {
                                    amount
                                    currencyCode
                                }
                                subtotalAmount {
                                    amount
                                    currencyCode
                                }
                                totalAmount {
                                    amount
                                    currencyCode
                                }
                            }
                            merchandise {
                                ... on ProductVariant {
                                    id
                                    price {
                                        amount
                                        currencyCode
                                    }
                                    sku
                                    product {
                                        id
                                        title
                                        handle
                                        featuredImage {
                                            url
                                            id
                                        }
                                        metafields(
                                            identifiers: [
                                                {
                                                    namespace: "c_f"
                                                    key: "additional_items"
                                                }
                                                {
                                                    namespace: "c_f"
                                                    key: "substitutes"
                                                }
                                                {
                                                    namespace: "c_f"
                                                    key: "components"
                                                }
                                                {
                                                    namespace: "c_f"
                                                    key: "min_order_multiplier"
                                                }
                                                {
                                                    namespace: "c_f"
                                                    key: "min_order_quantity"
                                                }
                                                {
                                                    namespace: "c_f"
                                                    key: "unit_measurement"
                                                }
                                                {
                                                    namespace: "c_f"
                                                    key: "packing_unit"
                                                }
                                            ]
                                        ) {
                                            description
                                            id
                                            key
                                            namespace
                                            type
                                            updatedAt
                                            value
                                            createdAt
                                        }
                                    }
                                }
                            }
                            attributes {
                                key
                                value
                            }
                        }
                    }
                    pageInfo {
                        endCursor
                        hasNextPage
                        hasPreviousPage
                        startCursor
                    }
                }
                attributes {
                    key
                    value
                }
                cost {
                    totalAmount {
                        amount
                        currencyCode
                    }
                    subtotalAmount {
                        amount
                        currencyCode
                    }
                    totalTaxAmount {
                        amount
                        currencyCode
                    }
                    totalDutyAmount {
                        amount
                        currencyCode
                    }
                }
            }
        }
    `;
};

const shopifyAddNewProductsToCart = () => {
    return gql`
        mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cartId, lines: $lines) {
                cart {
                    id
                    checkoutUrl
                    totalQuantity
                    lines(first: 10) {
                        nodes {
                            quantity
                            merchandise {
                                ... on ProductVariant {
                                    id
                                }
                            }
                        }
                    }
                }
                userErrors {
                    code
                    field
                    message
                }
            }
        }
    `;
};

const shopifyUpdateCartLines = () => {
    return gql`
        mutation cartLinesUpdate(
            $cartId: ID!
            $lines: [CartLineUpdateInput!]!
        ) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
                cart {
                    id
                    createdAt
                    updatedAt
                    checkoutUrl
                    totalQuantity
                    lines(first: 200) {
                        edges {
                            node {
                                id
                                quantity
                                cost {
                                    amountPerQuantity {
                                        amount
                                        currencyCode
                                    }
                                    subtotalAmount {
                                        amount
                                        currencyCode
                                    }
                                    totalAmount {
                                        amount
                                        currencyCode
                                    }
                                }
                                merchandise {
                                    ... on ProductVariant {
                                        id
                                        price {
                                            amount
                                            currencyCode
                                        }
                                        sku
                                        product {
                                            id
                                            title
                                            featuredImage {
                                                url
                                                id
                                            }
                                        }
                                    }
                                }
                                attributes {
                                    key
                                    value
                                }
                            }
                        }
                        pageInfo {
                            endCursor
                            hasNextPage
                            hasPreviousPage
                            startCursor
                        }
                    }
                    attributes {
                        key
                        value
                    }
                    cost {
                        totalAmount {
                            amount
                            currencyCode
                        }
                        subtotalAmount {
                            amount
                            currencyCode
                        }
                        totalTaxAmount {
                            amount
                            currencyCode
                        }
                        totalDutyAmount {
                            amount
                            currencyCode
                        }
                    }
                }
            }
        }
    `;
};

const shopifyCartLineRemove = () => {
    return gql`
        mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
            cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
                cart {
                    id
                    createdAt
                    updatedAt
                    checkoutUrl
                    totalQuantity
                    lines(first: 200) {
                        edges {
                            node {
                                id
                                quantity
                                cost {
                                    amountPerQuantity {
                                        amount
                                        currencyCode
                                    }
                                    subtotalAmount {
                                        amount
                                        currencyCode
                                    }
                                    totalAmount {
                                        amount
                                        currencyCode
                                    }
                                }
                                merchandise {
                                    ... on ProductVariant {
                                        id
                                        price {
                                            amount
                                            currencyCode
                                        }
                                        sku
                                        product {
                                            id
                                            title
                                            featuredImage {
                                                url
                                                id
                                            }
                                        }
                                    }
                                }
                                attributes {
                                    key
                                    value
                                }
                            }
                        }
                        pageInfo {
                            endCursor
                            hasNextPage
                            hasPreviousPage
                            startCursor
                        }
                    }
                    attributes {
                        key
                        value
                    }
                    cost {
                        totalAmount {
                            amount
                            currencyCode
                        }
                        subtotalAmount {
                            amount
                            currencyCode
                        }
                        totalTaxAmount {
                            amount
                            currencyCode
                        }
                        totalDutyAmount {
                            amount
                            currencyCode
                        }
                    }
                }
                userErrors {
                    field
                    message
                }
            }
        }
    `;
};

const shopifyCartAttributesUpdate = () => {
    return gql`
        mutation cartAttributesUpdate(
            $attributes: [AttributeInput!]!
            $cartId: ID!
        ) {
            cartAttributesUpdate(attributes: $attributes, cartId: $cartId) {
                cart {
                    id
                    checkoutUrl
                    totalQuantity
                }
                userErrors {
                    field
                    message
                }
            }
        }
    `;
};

export {
    createShopifyCart,
    retrieveShopifyCart,
    shopifyAddNewProductsToCart,
    shopifyUpdateCartLines,
    shopifyCartLineRemove,
    shopifyCartAttributesUpdate,
};
